import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, queryAll, state} from "lit/decorators.js";
import {Promises, schedule} from "../../common/utils/promises";
import {resolve} from "../../container";
import {Initializing, UnLitElement, WithEopSlots} from "../../common/elements";
import type {EopMacherbusRankedProject} from "./macherbusRankedProject";
import type {Ranking} from "./macherbusService";
import {MacherbusService} from "./macherbusService";
import {LocatedDates} from "../../common/utils/dates/locatedDates";
import {Dictionary} from "../../page/elements/dictionary";

@customElement("eop-macherbus-ranking")
export class EopMacherbusRanking extends WithEopSlots(Initializing(UnLitElement)) {

    @property({attribute: "voting-id"})
    private votingId: string;
    @property({attribute: "language"})
    private language: string;
    @state()
    private snapshotTime: Date = new Date();
    @queryAll("eop-macherbus-ranked-project")
    private projects: NodeListOf<EopMacherbusRankedProject>;

    private dictionary: Dictionary;

    public constructor(
        private promises: Promises = resolve(Promises),
        private macherbusService: MacherbusService = resolve(MacherbusService),
        private dates: LocatedDates = resolve(LocatedDates)
    ) {
        super();
    }

    public render(): TemplateResult {
        return html`
            <div class="macherbus-module">
                <eop-overlay-spinner event="ranked" class="ranking-spinner"></eop-overlay-spinner>
                <div class="head">
                    <div class="snapshot-time">${this.dictionary.translate("SNAPSHOT_TIME_LABEL")} ${this.dates.toLocalDateString(this.snapshotTime)}, ${this.dates.toLocalTimeString(this.snapshotTime)}</div>
                </div>
                <div class="macherbus-projects">${this.resolveSortedProjects()}</div>
            </div>
        `;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        void this.init();
    }

    private async init(): Promise<void> {
        const rankProjectsPromise = this.rankProjects(this.votingId);
        const spinnerForRanking = this.promises.decoratorFor("ranked")(rankProjectsPromise);
        await schedule(spinnerForRanking).as("ranking");
        this.initialized();
    }

    public async rankProjects(votingId: string): Promise<void> {
        const ranking = await this.macherbusService.getRanking(
            votingId,
            this.language,
            Array.from(this.projects).map(p => p.projectId));
        this.snapshotTime = new Date(ranking.snapshotTime);
        this.updateRanking(ranking);
    }

    private updateRanking(ranking: Ranking): void {
        ranking.rankedProjects.forEach(project => {
            Array.from(this.projects)
                .findFirst(p => p.projectId === project.projectId)
                ?.setRanking(project.rank, project.voteCount);
        });
    }

    private resolveSortedProjects(): EopMacherbusRankedProject[] {
        return Array.from(this.resolveSlots<EopMacherbusRankedProject>("macherbus-project"))
            .sort((a, b) => a.compareTo(b));
    }
}